import { memo, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import useSWR from "swr";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { ConnectionData, useConnectionStore } from "stores/ConnectionStore";
import { useFlairStore } from "stores/FlairStore";
import { SecretState, swrFetcher } from "stores/lib";
import {
  OAuth2Progress,
  OAuth2URLSuffix,
  useOAuth2Store,
} from "stores/OAuth2Store";
import { useOrganizationStore } from "stores/OrganizationStore";

import { captureSentryException } from "utility/capture_sentry_exception";
import { getServicesByName, Service } from "utility/connections";
import { GenericObject } from "utility/misc";

import ExternalDestinations from "external/destinations";

import LoaderModal from "layouts/components/LoaderModal";

// -----------------------------------------------------------------------------

enum Phase {
  OAuthProgress,
  LoadSecrets,
  Error,
  Done,
}

const OAuthDestinationToURLSuffix: GenericObject = {
  slack: OAuth2URLSuffix.SLACK,
};

// -----------------------------------------------------------------------------

const EnvironmentDestinationsOAuth = memo(
  function EnvironmentDestinationsOAuth() {
    const [oauthStore, oauthActions] = useOAuth2Store();
    const [, connectionActions] = useConnectionStore();
    const [, flairActions] = useFlairStore();
    const [organizationStore] = useOrganizationStore();

    const [phase, setPhase] = useState(Phase.OAuthProgress);
    const [refreshInterval, setRefreshInterval] = useState(2000);
    const [secretUuid, setSecretUuid] = useState("");
    const [connectionUuid, setConnectionUuid] = useState("");
    const [destination, setDestination] = useState<Service | null>(null);
    const [errorMessage, setErrorMessage] = useState("");

    const searchParams = useSearchParams()[0];
    const service = searchParams.get("service") ?? "none";

    const navigate = useNavigate();

    const destinationsByName = getServicesByName(ExternalDestinations.services);

    //
    useSWR<{
      count: number;
      next: any;
      previous: any;
      results: ConnectionData[];
    }>(
      "api/organization/connections",
      phase === Phase.LoadSecrets
        ? async (url) =>
            await swrFetcher(url, {
              method: "GET",
            })
        : null,
      {
        refreshInterval,
        onError: (error) => {
          const message = `Failed connecting to ${
            destination?.label ?? "Unknown"
          }`;

          captureSentryException(error, message);
          setRefreshInterval(0);
          setPhase(Phase.Error);

          toast.error(message, { position: "bottom-center" });
        },

        onSuccess: (data) => {
          if (data.count < 1) {
            return;
          }

          //
          const connections = data.results;
          const matchingConnection = connections.find((connection) => {
            if (!connection.client_secret) {
              return false;
            }

            //
            return connection.client_secret.uuid === secretUuid;
          });
          if (matchingConnection == null) {
            return;
          }

          //
          switch (matchingConnection.client_secret.state) {
            case SecretState.ERROR:
            case SecretState.INCORRECT: {
              const message = `Connected to ${
                destination?.label ?? "Unknown"
              }, but there was a problem.`;
              captureSentryException(new Error("Sentry error"), message);
              setRefreshInterval(0);
              setErrorMessage(message);
              setPhase(Phase.Error);
              connectionActions.loadConnections({ reload: true });
              break;
            }

            case SecretState.CORRECT:
              // celebration on first connection
              if (connections.length === 1) {
                flairActions.startCelebration();
              }

              //
              setRefreshInterval(0);
              setConnectionUuid(matchingConnection.uuid);
              toast.success(`Connected to ${destination?.label ?? "Unknown"}`, {
                position: "bottom-center",
              });

              setPhase(Phase.Done);
              connectionActions.loadConnections({ reload: true });
              break;
            default:
              break;
          }
        },
      }
    );

    if (
      phase === Phase.OAuthProgress &&
      oauthStore.progress < OAuth2Progress.Submitting
    ) {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const params = getParams(service, searchParams);

      if (error) {
        if (error === "access_denied") {
          setErrorMessage(
            `${error}: This may be due to canceling the destination or a permission problem.`
          );
        } else {
          setErrorMessage(`${error}`);
        }
        setPhase(Phase.Error);
      }

      oauthActions.submitParams(
        OAuthDestinationToURLSuffix[service],
        code,
        state,
        params
      );
    }

    useEffect(() => {
      setDestination(destinationsByName.get(service) ?? null);

      if (phase > Phase.OAuthProgress) return;

      switch (oauthStore.progress) {
        case OAuth2Progress.Success:
          setSecretUuid(oauthStore.uuid ?? "");
          setPhase(Phase.LoadSecrets);
          break;
        case OAuth2Progress.Error:
        case OAuth2Progress.ErrorOAuthParam:
          setErrorMessage(
            oauthStore.errorDetail ?? "An unknown error occurred."
          );
          setPhase(Phase.Error);
          break;

        default:
          break;
      }
    }, [
      oauthStore.progress,
      oauthStore.uuid,
      oauthStore.errorDetail,
      service,
      phase,
      destinationsByName,
    ]);

    //
    return (
      <>
        {phase < Phase.Error ? <LoaderModal /> : null}

        {phase === Phase.Error ? (
          <Stack spacing={2} direction="column">
            <Typography variant="h5">
              There was an error connecting to the destination
            </Typography>
            <Alert severity="error">{errorMessage}</Alert>
            <Stack spacing={2} direction="row">
              <Button component={Link} to="/environment/destinations">
                Go back
              </Button>
              {destination?.onConnect ? (
                <Button
                  onClick={() => {
                    destination.onConnect(organizationStore, navigate);
                  }}
                >
                  Retry
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        {phase === Phase.Done ? (
          destination?.notifications ? (
            <Navigate
              to={`/environment/destinations/${
                destination.url ?? ""
              }/${connectionUuid}/notifications`}
            />
          ) : (
            <Navigate to="/environment/destinations" />
          )
        ) : null}
      </>
    );
  }
);

export default EnvironmentDestinationsOAuth;

function getParams(
  destination: string,
  searchParams: URLSearchParams
): GenericObject | undefined {
  switch (destination.toLowerCase()) {
    case "pagerduty":
      return {
        subdomain: searchParams.get("subdomain"),
      };
    default:
      return undefined;
  }
}
